/**
 * Application Insights configuration for frontend telemetry
 * Provides differentiated logging from backend with proper service identification
 */

import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';

// Application Insights instance
let appInsights: ApplicationInsights | null = null;

/**
 * Initialize Application Insights for the frontend
 * Sets up telemetry with proper service identification
 * 
 * Security Note: Uses a frontend-safe connection string without managed identity
 * authentication. Only contains instrumentation key and ingestion endpoint,
 * which are safe to expose in client-side code per Microsoft's guidance.
 */
export const initializeAppInsights = (): void => {
  const connectionString = import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING || '';

  if (!connectionString) {
    console.warn('Application Insights connection string not found. Telemetry will be disabled.');
    return;
  }

  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString: connectionString,
        // Set explicit cloud role name for service differentiation
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxErrorStatusText: true,
        enableUnhandledPromiseRejectionTracking: true,
        // Disable console logging to reduce noise
        loggingLevelConsole: 0,
        loggingLevelTelemetry: 2
      }
    });

    appInsights.loadAppInsights();
    
    // Add telemetry initializer to set cloud role name
    appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
      if (envelope.tags) {
        envelope.tags['ai.cloud.role'] = 'MedMgmtAuth-Frontend';
        envelope.tags['ai.cloud.roleInstance'] = window.location.hostname;
      }
    });
    
    // Track initial page view with custom properties
    appInsights.trackPageView({
      name: 'Application Start',
      properties: {
        environment: import.meta.env.MODE,
        version: '1.0.0',
        userAgent: navigator.userAgent
      }
    });

    console.log('Application Insights initialized for MedMgmtAuth-Frontend');
  } catch (error) {
    console.error('Failed to initialize Application Insights:', error);
  }
};

/**
 * Get the Application Insights instance
 * Returns null if not initialized
 */
export const getAppInsights = (): ApplicationInsights | null => {
  return appInsights;
};

/**
 * Track a custom event with Application Insights
 */
export const trackEvent = (name: string, properties?: Record<string, any>, measurements?: Record<string, number>): void => {
  if (appInsights) {
    appInsights.trackEvent({ name, properties, measurements });
  }
};

/**
 * Track an exception with Application Insights
 */
export const trackException = (error: Error, properties?: Record<string, any>): void => {
  if (appInsights) {
    appInsights.trackException({ 
      exception: error, 
      properties: {
        ...properties,
        component: 'MedMgmtAuth-Frontend'
      }
    });
  }
};

/**
 * Track a custom metric with Application Insights
 */
export const trackMetric = (name: string, average: number, properties?: Record<string, any>): void => {
  if (appInsights) {
    appInsights.trackMetric({ name, average, properties });
  }
};

/**
 * Track user authentication events
 */
export const trackAuthEvent = (eventType: 'login_success' | 'login_failure' | 'logout', properties?: Record<string, any>): void => {
  trackEvent(`auth_${eventType}`, {
    ...properties,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track API call performance
 */
export const trackApiCall = (endpoint: string, duration: number, success: boolean, statusCode?: number): void => {
  trackEvent('api_call', {
    endpoint,
    success,
    statusCode: statusCode?.toString(),
    component: 'frontend'
  }, {
    duration
  });
};

export default {
  initialize: initializeAppInsights,
  getInstance: getAppInsights,
  trackEvent,
  trackException,
  trackMetric,
  trackAuthEvent,
  trackApiCall
};
